import { CoreService } from '@ats/ats-platform-dashboard';

import * as d3 from 'd3';
import * as _ from 'lodash';
import { TopologyDevice } from '../domain/dataModels/TopologyDevice';
import { BaseSvg } from '@ats/ats-platform-dashboard';
//http://bl.ocks.org/caged/6476579
export class DeviceTopology extends BaseSvg<any> {

  //Public info
  public data: TopologyDevice[];
  public rows: number;
  public columns: number;
  public marginBetweenX: number;
  public marginBetweenY: number;
  public measurementUnit: string = '';
  public unitWidth: number;
  public unitHeight: number;
  public mainPosX: any[] = [];
  public mainPosY: any[] = [];
  public meterRows: number;
  public meterColumns: number;
  public formatValue: (n: number | { valueOf(): number }) => string;

  //Private info
  private posArrUnit: { x: number, y: number }[][];

  constructor(private core: CoreService) {
    super();
    this.formatValue = d3.format('.1f');
  }

  public init(group: d3.Selection<SVGGElement, unknown, HTMLElement, unknown>) {
    this.group = group;
  }

  public draw() {
    this.group.selectAll("*").remove();
    this.group.selectChildren('*').remove();
    this.group.html("");
    var gMainGroup = this.group.append('g').attr('id', 'gMainGroup').attr('transform', 'translate(' + this.left + ',' + this.top + ')');

    this.initZoom();

    this.posArrUnit = this.convertArea(
      this.unitWidth,
      this.unitHeight,
      this.meterRows,
      this.meterColumns
    );

    this.drawUnits(this.data, gMainGroup, this.posArrUnit, this.unitWidth, this.unitHeight);
  }

  public handleZoom(e) {
    this.group.select('#gMainGroup').attr('transform', e.transform);

    const threshold = 1.75;

    this.group.selectAll('g.zoom-0').attr('visibility', e.transform.k < threshold ? 'visible' : 'hidden');
    this.group.selectAll('g.zoom-1').attr('visibility', e.transform.k >= threshold ? 'visible' : 'hidden');
  }

  public initZoom() {
    let zoom = d3.zoom().on('zoom', this.handleZoom.bind(this));
    this.group
      .call(zoom);
  }

  private drawUnits(units: TopologyDevice[], gMainGroup, posArrUnit, unitWidth, unitHeight) {
    units.forEach((currUnit, index) => {
      units[index].G = this.drawUnitBase(
        gMainGroup,
        index,
        currUnit,
        this.data,
        posArrUnit,
        unitWidth,
        unitHeight
      );
      if (currUnit.Children && currUnit.Children.length > 0) {
        this.drawUnits(currUnit.Children, gMainGroup, posArrUnit, unitWidth, unitHeight);
      }
    });
  }

  private drawUnitBase(g, i: number, currUnit: TopologyDevice, units: TopologyDevice[], posArrUnit, width: number, height: number) {
    currUnit.Info = {};
    var unit = g
      .append('g')
      .attr('id', 'unit ' + i)
      .attr('transform', 'translate(' + currUnit.X + ' , ' + currUnit.Y + ')');

    unit
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .attr('rx', 5)
      .attr('stroke', 'grey');

    unit
      .append('rect')
      .attr('x', width * 0.05)
      .attr('y', height * 0.15)
      .attr('width', width - (width * 0.05 * 2))
      .attr('height', height * 0.7)
      .attr('fill', 'blue')
      .attr('rx', 5)
      .attr('stroke', 'grey')
      .style('opacity', 0.1);

    const iconName = currUnit.IconName ?? 'electricity';
    const iconScale = currUnit.IconScale ?? 0.02;
    currUnit.Info.Icon = this.appendPath(
      iconName,
      unit,
      posArrUnit[0][1].x,
      posArrUnit[0][1].y,
      'grey',
      iconScale,
      { classId: 'icon', moveDown: 0, moveRight: 0 }
    );
    if(currUnit.PowerTagId){
      unit.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', posArrUnit[1][0].x)
      .attr('y', posArrUnit[1][0].y + 3)
      .style('fill', 'grey')
      .attr('class', 'currValues')
      .style('font-size', 8)
      .text('P');
    currUnit.Info.TextPower = unit.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', posArrUnit[1][1].x)
      .attr('y', posArrUnit[1][1].y + 3)
      .style('fill', '#5282c6')
      .attr('class', 'currValues')
      .style('font-weight', 600)
      .style('font-size', 8)
      .text('---');
    currUnit.Info.MeasurementPower = unit.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', posArrUnit[1][2].x - 2)
      .attr('y', posArrUnit[1][2].y + 3)
      .style('fill', 'grey')
      .style('font-size', 8)
      .text('kW');
    }
    if(currUnit.EnergyInTagId){
      var importIcon = this.appendPath(
        'arrowRight',
        unit,
        posArrUnit[2][0].x,
        posArrUnit[2][0].y,
        'grey',
        0.015,
        { classId: 'icon', moveDown: 0, moveRight: 0 }
      );
      currUnit.Info.TextEnergyImport = unit.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', posArrUnit[2][1].x)
        .attr('y', posArrUnit[2][1].y + 3)
        .style('fill', '#5282c6')
        .attr('class', 'currValues')
        .style('font-weight', 600)
        .style('font-size', 8)
        .text(this.formatValue(currUnit.LatestPoint.EnergyIn) ?? '---');
      currUnit.Info.MeasurementEnergyImport = unit.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', posArrUnit[2][2].x)
        .attr('y', posArrUnit[2][2].y + 3)
        .style('fill', 'grey')
        .style('font-size', 8)
        .text('kWh');
    }
    if(currUnit.EnergyOutTagId){
      var exportIcon = this.appendPath(
        'arrowLeft',
        unit,
        posArrUnit[3][0].x,
        posArrUnit[3][0].y,
        'grey',
        0.015,
        { classId: 'icon', moveDown: 0, moveRight: 0 }
      );
      currUnit.Info.TextEnergyExport = unit.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', posArrUnit[3][1].x)
        .attr('y', posArrUnit[3][1].y + 3)
        .style('fill', '#5282c6')
        .attr('class', 'currValues')
        .style('font-weight', 600)
        .style('font-size', 8)
        .text(this.formatValue(currUnit.LatestPoint.EnergyOut) ?? '---');
      currUnit.Info.MeasurementEnergyExport = unit.append('text')
        .attr('text-anchor', 'middle')
        .attr('x', posArrUnit[3][2].x)
        .attr('y', posArrUnit[3][2].y + 3)
        .style('fill', 'grey')
        .style('font-size', 8)
        .text('kWh');
    }
    if(currUnit.ChartId){
      this.appendChart(
        currUnit.ChartId,
        unit,
        '#5282c6',
        posArrUnit[4][1].x,
        posArrUnit[4][1].y,
        {assetId: currUnit.PowerTag?.AssetId ?? ''}
      )
    }
    unit.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', posArrUnit[5][1].x)
      .attr('y', posArrUnit[5][1].y + 3)
      .style('fill', 'dark grey')
      .style('font-size', 8)
      .style('font-weight', 400)
      .text(currUnit.Name);

    if (currUnit.ParentDeviceName) {
      this.appendLink(
        unit,
        currUnit,
        units,
        width,
        height
      )
    }
    return unit;
  }

  private convertArea(
    width,
    height,
    rows,
    columns,
    left = 0,
    right = 0,
    top = 0,
    bottom = 0
  ) {
    const columnWidth =
      (width - left - right) / columns;
    const rowHeight =
      (height - top - bottom) / rows;
    var startX = left;
    var startY = top;
    var arr = [];
    var obj = {};

    for (let i = 0; i < rows; i++) {
      arr[i] = [];
      for (let j = 0; j < columns; j++) {
        var currX = startX + j * columnWidth + columnWidth / 2;
        var currY = startY + i * rowHeight + rowHeight / 2;
        arr[i][j] = { x: currX, y: currY };
      }
    }
    return arr;
  }
  private appendPath(
    icon,
    group,
    x,
    y,
    color,
    scale = 1,
    { icons = this.icons, classId = '', moveUp = 0, moveDown = 0, moveLeft = 0, moveRight = 0, } = {}
  ) {
    var icon = group
      .append('path')
      .attr('d', icons[icon])
      .attr('fill', color)
      .attr('class', classId);
    var bBox = icon.node().getBBox();

    icon.attr(
      'transform',
      'translate(' +
      (x + moveRight - moveLeft - (bBox.width * scale) / 2) +
      ',' +
      (y - moveUp + moveDown - (bBox.height * scale) / 2) +
      ')scale(' +
      scale +
      ')'
    );
    return icon;
  }
  private appendChart(
    targetId,
    group,
    color,
    x,
    y,
    {icons = this.icons, classId = '', moveUp = 0, moveDown = 0, moveLeft = 0, moveRight = 0, assetId = ''} = {}
  ) {
    var gButton = group.append('g')
      .attr('transform', 'translate(' + x + ',' + y + ')');

    var buttonRect = gButton.append('rect')
      .attr('x', -12 + moveRight - moveLeft)
      .attr('y', -7 + moveDown - moveUp)
      .attr('width', 24)
      .attr('height', 16)
      .attr('rx', 2)
      .attr('stroke', 'none')
      .attr('fill', 'lightgrey')
      .attr('opacity', 0);

    var buttonPath = this.appendPath(
      'chart',
      gButton,
      0,
      0,
      color,
      0.025,
      { icons: icons, classId: classId, moveUp: moveUp, moveDown: moveDown, moveLeft: moveLeft, moveRight: moveRight}
    );

    buttonPath.attr('opacity', 1);

    gButton.style('cursor', 'pointer');
    gButton.on('mouseover', (e) => {
      buttonRect.attr('opacity', '0.8');
    });
    gButton.on('mouseout', (e) => {
      buttonRect.attr('opacity', '0');
    });
    gButton.on('click', (e) => {
      if(assetId === ''){
        this.core.navigate(['chart', targetId]);
      } else {
        this.core.navigate(['chart', targetId, 'asset', assetId]);
      }
    });

    return gButton;
  }
  private appendLink(
    group,
    currUnit: TopologyDevice,
    units: TopologyDevice[],
    unitWidth: number,
    unitHeight: number
  ) {
      var parentUnit = this.findNodeByNameBFS(units, currUnit.ParentDeviceName);  

      var childX1 = unitWidth / 2;
      var childY1 = 0;
      var childX2 = childX1;
      var childY2 = parentUnit.Y + unitHeight - currUnit.Y + this.marginBetweenY / 2;
      var parentX2 = parentUnit.X - currUnit.X + unitWidth / 2;
      var parentY2 = childY2
      var parentX1 = parentX2;
      var parentY1 = childY2 - this.marginBetweenY / 2;;

      var childLine = group.append('line')
        .attr("x1", childX1)
        .attr("y1", childY1)
        .attr("x2", childX2)
        .attr("y2", childY2)
        .attr("stroke-width", 1)
        .attr("stroke", "grey");

      if (childX2 !== parentX2) {
        var middleLine = group.append('line')
          .attr("x1", childX2)
          .attr("y1", childY2)
          .attr("x2", parentX2)
          .attr("y2", parentY2)
          .attr("stroke-width", 1)
          .attr("stroke", "grey");
      }

      var parentLine = group.append('line')
        .attr("x1", parentX1)
        .attr("y1", parentY1)
        .attr("x2", parentX2)
        .attr("y2", parentY2)
        .attr("stroke-width", 1)
        .attr("stroke", "grey");

    };

  private findNodeByNameBFS(roots: TopologyDevice[], name: string) {
    const queue = [...roots];
    while (queue.length > 0) {
      const node = queue.shift();
      if (node.Name === name) {
        return node;
      }
      if (node.Children) {
        for (const child of node.Children) {
          queue.push(child);
        }
      }
    }
    return null;
  }

  private icons: {} = {
    carCharger: 'M96 0C60.7 0 32 28.7 32 64V448c-17.7 0-32 14.3-32 32s14.3 32 32 32H320c17.7 0 32-14.3 32-32s-14.3-32-32-32V304h16c22.1 0 40 17.9 40 40v32c0 39.8 32.2 72 72 72s72-32.2 72-72V252.3c32.5-10.2 56-40.5 56-76.3V144c0-8.8-7.2-16-16-16H544V80c0-8.8-7.2-16-16-16s-16 7.2-16 16v48H480V80c0-8.8-7.2-16-16-16s-16 7.2-16 16v48H432c-8.8 0-16 7.2-16 16v32c0 35.8 23.5 66.1 56 76.3V376c0 13.3-10.7 24-24 24s-24-10.7-24-24V344c0-48.6-39.4-88-88-88H320V64c0-35.3-28.7-64-64-64H96zM216.9 82.7c6 4 8.5 11.5 6.3 18.3l-25 74.9H256c6.7 0 12.7 4.2 15 10.4s.5 13.3-4.6 17.7l-112 96c-5.5 4.7-13.4 5.1-19.3 1.1s-8.5-11.5-6.3-18.3l25-74.9H96c-6.7 0-12.7-4.2-15-10.4s-.5-13.3 4.6-17.7l112-96c5.5-4.7 13.4-5.1 19.3-1.1z',
    battery: 'M464 160c8.8 0 16 7.2 16 16V336c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16H464zM80 96C35.8 96 0 131.8 0 176V336c0 44.2 35.8 80 80 80H464c44.2 0 80-35.8 80-80V320c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32V176c0-44.2-35.8-80-80-80H80zm208 96H96V320H288V192z',
    electricityPole:
      'M215.841,202.648,61.029,264.572A8,8,0,0,0,64,280H80v40a8,8,0,0,0,16,0V280h48v40a8,8,0,0,0,16,0V280h51.544l-3.529,63.509v.03l-8,144.018a8,8,0,0,0,12.425,7.1L256,465.615l43.562,29.041a8,8,0,0,0,12.425-7.1l-8-144.018v-.03L300.456,280H352v40a8,8,0,0,0,16,0V280h48v40a8,8,0,0,0,16,0V280h16a8,8,0,0,0,2.971-15.428L296.159,202.648,292.012,128H368v40a8,8,0,0,0,16,0V128h16a8,8,0,0,0,3.765-15.059L285.334,49.778,262.4,19.2a8,8,0,0,0-12.8,0L226.666,49.778,108.235,112.941A8,8,0,0,0,112,128h16v40a8,8,0,0,0,16,0V128h75.988Zm76.85,226.011L266.174,394.91l23.015-29.292Zm-69.88-63.041,23.015,29.292-26.517,33.749Zm23.707-207.9L233.2,178.644l2.139-38.5ZM246.573,128h18.854L256,142.814ZM256,172.615,273.427,200H238.573ZM244.686,304l-19.537,19.536,2.057-37.016Zm-1.372-24h25.372L256,292.686Zm-16.638,64.639L256,315.314l29.324,29.325L256,381.961ZM267.314,304l17.48-17.48,2.057,37.016Zm-38.857-40,2.666-48h49.753l2.667,48Zm37.025-106.286,11.18-17.568,2.139,38.5Zm23.294-87.967,29.059,15.5-27.019,21.229ZM272.432,64l2.667,48H236.9l2.667-48Zm-51.3,43.373-27.152-22.03,29.242-15.6ZM212.7,259.119,181.047,233.8l33.815-13.526Zm-48.732-18.491L193.192,264H105.541Zm96.466,208.716a8,8,0,0,0-8.876,0l-34.687,23.125.886-15.941L256,407.859l38.239,48.669.886,15.941ZM318.808,264l29.22-23.372L406.459,264Zm12.145-30.2L299.3,259.119l-2.158-38.847ZM309.681,112l23.581-18.527L368,112ZM256,37.333,264,48H248ZM178.659,93.515,201.442,112H144Z',
    solarPanel: 'M115.2 0C84.7 0 58.5 21.5 52.5 51.4L1.3 307.4C-6.6 347 23.6 384 64 384H281v64H217c-17.7 0-32 14.3-32 32s14.3 32 32 32H409c17.7 0 32-14.3 32-32s-14.3-32-32-32H345V384H562c40.4 0 70.7-36.9 62.8-76.6l-51.2-256C567.5 21.5 541.3 0 510.8 0H115.2zM253.9 64H372.1l10.4 104h-139L253.9 64zM195.3 168H94.4L115.2 64h90.4L195.3 168zM84.8 216H190.5L180.1 320H64L84.8 216zm153.9 0H387.3l10.4 104-169.4 0 10.4-104zm196.8 0H541.2L562 320h-116L435.5 216zm96-48H430.7L420.3 64h90.4l31.4-6.3L510.8 64l20.8 104z',
    electricity:
      'M296 160H180.6l42.6-129.8C227.2 15 215.7 0 200 0H56C44 0 33.8 8.9 32.2 20.8l-32 240C-1.7 275.2 9.5 288 24 288h118.7L96.6 482.5c-3.6 15.2 8 29.5 23.3 29.5 8.4 0 16.4-4.4 20.8-12l176-304c9.3-15.9-2.2-36-20.7-36z',
    gas: 'M192 0C79.7 101.3 0 220.9 0 300.5 0 425 79 512 192 512s192-87 192-211.5c0-79.9-80.2-199.6-192-300.5zm0 448c-56.5 0-96-39-96-94.8 0-13.5 4.6-61.5 96-161.2 91.4 99.7 96 147.7 96 161.2 0 55.8-39.5 94.8-96 94.8z',
    electricalMeter: 'm 314.88 352.8 h 16.801 v 16.801 H 314.88 Z m -42 0 h 16.801 v 16.801 H 272.88 Z m -42 0 h 16.801 v 16.801 H 230.88 Z m -42 0 h 16.801 v 16.801 H 188.88 Z M 364.16 311.92 H 155.84 c -4.4805 0 -8.3984 3.9219 -8.3984 8.3984 v 79.52 c 0 4.4805 3.9219 8.3984 8.3984 8.3984 h 208.32 c 4.4805 0 8.3984 -3.9219 8.3984 -8.3984 v -79.52 c 0 -4.4805 -3.918 -8.3984 -8.3984 -8.3984 z m -8.3984 79.52 h -191.52 v -62.723 h 191.52 z M 461.6 153.44 c 0 -20.16 -15.121 -36.398 -35.281 -37.52 V 19.6 c 0 -4.4805 -3.9219 -8.4023 -8.3984 -8.4023 h -315.28 c -4.4805 0 -8.3984 3.9219 -8.3984 8.3984 v 96.32 c -20.16 1.1211 -35.84 16.801 -35.84 37.52 c 0 10.078 3.9219 19.602 11.199 26.879 c 7.2812 6.1602 15.68 10.078 24.641 10.641 v 348.88 c 0 4.4805 3.9219 8.3984 8.3984 8.3984 h 315.28 c 4.4805 0 8.3984 -3.9219 8.3984 -8.3984 v -348.88 c 8.3984 -0.5586 17.359 -4.4805 24.641 -10.641 c 6.7188 -7.2812 10.641 -16.801 10.641 -26.879 z m -380.24 14.559 c -3.9219 -3.9219 -6.1602 -8.9609 -6.1602 -14.559 c 0 -11.199 8.3984 -19.602 19.039 -20.719 V 173.6 c -4.4766 0 -9.5195 -2.2422 -12.879 -5.6016 z m 283.92 363.44 H 154.72 v -75.602 h 210.56 z m 44.238 0 h -27.441 v -84 c 0 -4.4805 -3.9219 -8.3984 -8.3984 -8.3984 l -227.36 -0.004 c -4.4805 0 -8.3984 3.9219 -8.3984 8.3984 v 84 h -26.879 l -0.004 -503.44 h 298.48 z m 29.68 -363.44 c -3.9219 3.3594 -8.3984 5.6016 -12.879 6.1602 v -40.879 c 10.641 1.1211 18.48 9.5195 18.48 20.719 c 0 5.0391 -2.2422 10.641 -5.6016 14 z M 301.44 133.84 H 279.6 l 14 -41.441 c 1.1211 -2.8008 0.5586 -5.6016 -1.1211 -7.2812 c -1.6797 -2.2383 -3.9219 -3.3594 -6.7188 -3.3594 h -38.078 c -3.3594 0 -6.7188 2.2383 -7.8398 5.6016 l -29.125 80.641 c -1.1211 2.8008 -0.5586 5.6016 1.1211 7.8398 c 1.6797 2.2383 3.9219 3.3594 6.7188 3.3594 h 24.641 l -3.9219 60.48 c 0 3.9219 2.2383 7.2812 6.1602 8.3984 c 0.5586 0 1.6797 0.5586 2.2383 0.5586 c 2.8008 0 5.6016 -1.6797 7.2812 -4.4805 l 53.762 -98 c 1.6797 -2.8008 1.1211 -5.6016 0 -8.3984 c -1.6797 -2.2383 -4.4805 -3.918 -7.2773 -3.918 z m -43.121 69.441 l 2.2383 -31.922 c 0 -2.2383 -0.5586 -4.4805 -2.2383 -6.1602 c -1.6797 -1.6797 -3.9219 -2.8008 -6.1602 -2.8008 h -21.84 L 252.7168 98 h 20.719 l -13.438 41.441 c -1.1211 2.8008 -0.5586 5.6016 1.1211 7.2812 c 1.6797 2.2383 3.9219 3.3594 6.7188 3.3594 h 19.602 z M 310.96 472.64 h 16.801 v 42.559 H 310.96 Z m -39.76 0 h 16.801 v 42.559 H 271.2 Z m -39.2 0 h 16.801 v 42.559 H 232 Z m -39.76 0 h 16.801 v 42.559 H 192.24 Z m -53.2 -190.4 h 241.92 c 4.4805 0 8.3984 -3.9219 8.3984 -8.3984 v -217.28 c 0 -4.4805 -3.9219 -8.3984 -8.3984 -8.3984 H 139.04 c -4.4805 0 -8.3984 3.9219 -8.3984 8.3984 v 217.28 c 0 4.4805 3.918 8.3984 8.3984 8.3984 z m 8.4023 -217.28 h 225.12 v 200.48 h -225.12 z',
    building: 'M64 48c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16h80V400c0-26.5 21.5-48 48-48s48 21.5 48 48v64h80c8.8 0 16-7.2 16-16V64c0-8.8-7.2-16-16-16H64zM0 64C0 28.7 28.7 0 64 0H320c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm88 40c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V104zM232 88h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V104c0-8.8 7.2-16 16-16zM88 232c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H104c-8.8 0-16-7.2-16-16V232zm144-16h48c8.8 0 16 7.2 16 16v48c0 8.8-7.2 16-16 16H232c-8.8 0-16-7.2-16-16V232c0-8.8 7.2-16 16-16z',
    computer: 'M384 96V320H64L64 96H384zM64 32C28.7 32 0 60.7 0 96V320c0 35.3 28.7 64 64 64H181.3l-10.7 32H96c-17.7 0-32 14.3-32 32s14.3 32 32 32H352c17.7 0 32-14.3 32-32s-14.3-32-32-32H277.3l-10.7-32H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm464 0c-26.5 0-48 21.5-48 48V432c0 26.5 21.5 48 48 48h64c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48H528zm16 64h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H544c-8.8 0-16-7.2-16-16s7.2-16 16-16zm-16 80c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H544c-8.8 0-16-7.2-16-16zm32 160a32 32 0 1 1 0 64 32 32 0 1 1 0-64z',
    arrowRight: 'M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z',
    arrowLeft: 'M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z',
    chart: 'M496 384H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v336c0 17.67 14.33 32 32 32h464c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16zM464 96H345.94c-21.38 0-32.09 25.85-16.97 40.97l32.4 32.4L288 242.75l-73.37-73.37c-12.5-12.5-32.76-12.5-45.25 0l-68.69 68.69c-6.25 6.25-6.25 16.38 0 22.63l22.62 22.62c6.25 6.25 16.38 6.25 22.63 0L192 237.25l73.37 73.37c12.5 12.5 32.76 12.5 45.25 0l96-96 32.4 32.4c15.12 15.12 40.97 4.41 40.97-16.97V112c.01-8.84-7.15-16-15.99-16z'
  };

}